import type { BalanceRow, CashPositionMonth, DashboardData, GospelMonth } from './api'

// Where the Overview's payroll figure comes from (Tyler 2026-10-28):
//
//   'payroll-tab'  payroll rebuilt from each row of the 'ZK Payroll' tab.
//   'gospel'       GOSPEL row 4 ('Actual AP (Confirmed)') as the sheet holds
//                  it, adjustments included (the *GospelAp API fields).
//
// After a preview, Tyler approved GOSPEL, so it is the only source today
// (PAYROLL_SOURCE_LOCKED below) and the payroll tab is the revert path. The
// source drives every chart built on the payroll total together (KPIs, cash in
// and out, cash position + scenario planner, the cash flow table, bank
// reconciliation), so no two panels answer different questions. Breakeven
// (software-team slice) and Plan vs actual payroll (freee) do not read the
// payroll total and stay as they are.

export type PayrollSource = 'payroll-tab' | 'gospel'

// Frontend rollback (kill switch): set this to false to hide the switch and
// force 'payroll-tab' for everyone, whatever they stored. It wins over
// PAYROLL_SOURCE_LOCKED.
export const GOSPEL_SOURCE_AVAILABLE = true

// Tyler approved the GOSPEL total (Slack, after the 2026-10-28 preview), so
// the switch is retired: while this is set, the switch is hidden (a one-line
// note says where payroll comes from instead), every viewer gets this source
// whatever they stored, and the data map draws only this source's arrows. The
// switch and the payroll-tab series are kept, not deleted. To bring the
// switch back, set this to null.
export const PAYROLL_SOURCE_LOCKED: PayrollSource | null = 'gospel'

// Whether the page offers the switch at all.
export const PAYROLL_SOURCE_SWITCH_SHOWN = PAYROLL_SOURCE_LOCKED === null && GOSPEL_SOURCE_AVAILABLE

// The source the page reads: the kill switch first, then the lock, then the
// viewer's stored choice.
export function resolvePayrollSource(
  stored: PayrollSource,
  locked: PayrollSource | null,
  available: boolean,
): PayrollSource {
  if (!available) return 'payroll-tab'
  return locked ?? stored
}

export function currentPayrollSource(stored: PayrollSource): PayrollSource {
  return resolvePayrollSource(stored, PAYROLL_SOURCE_LOCKED, GOSPEL_SOURCE_AVAILABLE)
}

// The one source every viewer is forced onto, or null while the per-viewer
// switch is offered. The kill switch forces the payroll tab even with the lock
// off, so it pins too; otherwise the data map would draw GOSPEL arrows into
// charts that no longer read it.
export function pinnedPayrollSource(locked: PayrollSource | null, available: boolean): PayrollSource | null {
  if (!available) return 'payroll-tab'
  return locked
}

// The data map draws this source's arrows only.
export const PINNED_PAYROLL_SOURCE: PayrollSource | null = pinnedPayrollSource(
  PAYROLL_SOURCE_LOCKED,
  GOSPEL_SOURCE_AVAILABLE,
)

export const PAYROLL_SOURCE_STORAGE_KEY = 'zk-finance.payrollSource.v1'

export function readPayrollSource(storage: Storage | null): PayrollSource {
  try {
    return storage?.getItem(PAYROLL_SOURCE_STORAGE_KEY) === 'gospel' ? 'gospel' : 'payroll-tab'
  } catch {
    return 'payroll-tab'
  }
}

export function writePayrollSource(storage: Storage | null, source: PayrollSource): void {
  try {
    storage?.setItem(PAYROLL_SOURCE_STORAGE_KEY, source)
  } catch {
    // Not remembering is fine; the choice still applies for this visit.
  }
}

export type SourceSeries = Pick<
  DashboardData,
  | 'gospelCashFlow'
  | 'gospelCashFlowGospelAp'
  | 'cashPositionByMonth'
  | 'cashPositionByMonthGospelAp'
  | 'balanceReconciliation'
  | 'balanceReconciliationGospelAp'
>

// The three series that carry the payroll total, for the chosen source.
export function seriesFor(
  source: PayrollSource,
  data: SourceSeries,
): { gospel: GospelMonth[]; cash: CashPositionMonth[]; balance: BalanceRow[] } {
  return source === 'gospel'
    ? {
        gospel: data.gospelCashFlowGospelAp,
        cash: data.cashPositionByMonthGospelAp,
        balance: data.balanceReconciliationGospelAp,
      }
    : { gospel: data.gospelCashFlow, cash: data.cashPositionByMonth, balance: data.balanceReconciliation }
}
